import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Switch,
    FormControlLabel,
    CircularProgress,
    Snackbar,
    Alert,
    Grid,
    useTheme,
    useMediaQuery
} from '@mui/material';
import axiosInstance from '../utils/axiosInstance';
import apiConfig from '../config/appConfig';

const ApiSettings = () => {
    const [config, setConfig] = useState({
        enabled: true,
        windowMs: 15,
        max: 100,
        message: '',
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const fetchConfig = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance.get(apiConfig.endpoints.rateLimit.getConfig);
                setConfig(response.data);
            } catch (error) {
                console.error("Failed to fetch rate limit config", error);
                setSnackbar({ open: true, message: 'Failed to load configuration.', severity: 'error' });
            } finally {
                setLoading(false);
            }
        };
        fetchConfig();
    }, []);

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setConfig(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await axiosInstance.put(apiConfig.endpoints.rateLimit.updateConfig, config);
            setSnackbar({ open: true, message: 'Configuration saved successfully!', severity: 'success' });
        } catch (error) {
            console.error("Failed to save rate limit config", error);
            setSnackbar({ open: true, message: 'Failed to save configuration.', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /></Box>;
    }

    return (
        <Box sx={{ p: isMobile ? 2 : 3 }}>
            <Typography variant="h4" gutterBottom>API Rate Limiting</Typography>
            <Paper sx={{ p: isMobile ? 2 : 3, maxWidth: 800, margin: 'auto' }}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={<Switch checked={config.enabled} onChange={handleChange} name="enabled" />}
                            label="Enable Rate Limiting"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Time Window (ms)"
                            name="windowMs"
                            type="number"
                            value={config.windowMs}
                            onChange={handleChange}
                            disabled={!config.enabled}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Max Requests per Window"
                            name="max"
                            type="number"
                            value={config.max}
                            onChange={handleChange}
                            disabled={!config.enabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Message for Rate-Limited Users"
                            name="message"
                            multiline
                            rows={3}
                            value={config.message}
                            onChange={handleChange}
                            disabled={!config.enabled}
                        />
                    </Grid>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? <CircularProgress size={24} /> : 'Save Changes'}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>
            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ApiSettings; 